/**
 * Dashboard Page component
 * User dashboard showing projects and account info
 * Context: Main user workspace after authentication
 */

import { useEffect } from 'react';
import { 
  Container, 
  Title, 
  Text, 
  Button, 
  Stack, 
  Grid, 
  Card, 
  Group,
  Badge,
  ActionIcon,
  Menu,
  Skeleton
} from '@mantine/core';
import { 
  IconPlus, 
  IconVideo, 
  IconDots,
  IconEdit,
  IconTrash,
  IconShare,
  IconCoin
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { useProjectStore } from '../context/useProjectStore';
import { useUserStore } from '../context/userStore';

export default function DashboardPage() {
  const navigate = useNavigate();
  
  const { 
    projects, 
    isLoading: projectsLoading, 
    loadProjects,
    deleteProject 
  } = useProjectStore();
  
  const { user, isLoading: userLoading, loadUser } = useUserStore();

  useEffect(() => {
    loadUser();
    loadProjects();
  }, [loadUser, loadProjects]);

  const handleCreateProject = () => {
    navigate('/lab');
  };

  const handleDeleteProject = async (projectId: string) => {
    if (confirm('Are you sure you want to delete this project?')) {
      await deleteProject(projectId);
    }
  };

  return (
    <Container size="xl" py="xl">
      {/* Header with user info */}
      <Group justify="space-between" mb="xl">
        <Stack gap="xs">
          <Title order={1}>Your Video Lab</Title>
          <Text color="dimmed">
            Transform YouTube videos into interactive learning experiences
          </Text>
        </Stack>
        
        {userLoading ? (
          <Skeleton height={40} width={120} />
        ) : user ? (
          <Group gap="md">
            <Badge 
              leftSection={<IconCoin size={14} />}
              variant="gradient" 
              gradient={{ from: 'warm-gold', to: 'neural-blue' }}
              size="lg"
            >
              {user.credits} Credits
            </Badge>
            <Badge variant="outline" size="lg">
              {user.subscriptionTier === 'pro' ? 'Pro Member' : 'Free Account'}
            </Badge>
          </Group>
        ) : null}
      </Group>

      {/* Quick actions */}
      <Card shadow="sm" padding="lg" mb="xl">
        <Group justify="space-between">
          <Group gap="md">
            <IconVideo size={24} color="var(--mantine-color-blue-6)" />
            <Stack gap={0}>
              <Text fw={500}>Start a New Project</Text>
              <Text size="sm" color="dimmed">
                Paste any YouTube URL to begin analyzing and creating
              </Text>
            </Stack>
          </Group>
          <Button 
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateProject}
            size="lg"
          >
            New Project
          </Button>
        </Group>
      </Card>

      {/* Projects grid */}
      <Title order={2} mb="md">
        Recent Projects
      </Title>
      
      {projectsLoading ? (
        <Grid>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid.Col span={{ md: 4 }} key={index}>
              <Card shadow="sm" padding="lg">
                <Skeleton height={120} mb="md" />
                <Skeleton height={16} mb="xs" />
                <Skeleton height={12} width="80%" mb="md" />
                <Group justify="space-between">
                  <Skeleton height={20} width={60} />
                  <Skeleton height={24} width={24} />
                </Group>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      ) : projects.length === 0 ? (
        <Card shadow="sm" padding="xl" style={{ textAlign: 'center' }}>
          <IconVideo size={48} color="var(--mantine-color-dimmed)" />
          <Title order={3} mt="md">No projects yet</Title>
          <Text color="dimmed" mb="md">
            Create your first project to start transforming YouTube videos into learning experiences
          </Text>
          <Button onClick={handleCreateProject}>
            Create First Project
          </Button>
        </Card>
      ) : (
        <Grid>
          {projects.map((project) => (
            <Grid.Col span={{ md: 4 }} key={project.id}>
              <Card 
                shadow="sm" 
                padding="lg" 
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/lab/${project.id}`)}
              >
                {/* Project thumbnail */}
                <Card.Section>
                  <div
                    style={{
                      height: 120,
                      backgroundImage: `url(${project.videoMetadata.thumbnail})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        background: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        color: 'white',
                        fontSize: '12px',
                      }}
                    >
                      {Math.floor(project.videoMetadata.duration / 60)}m
                    </div>
                  </div>
                </Card.Section>

                {/* Project info */}
                <Group justify="space-between" mt="md">
                  <Stack gap="xs" style={{ flex: 1 }}>
                    <Text fw={500} lineClamp={1}>
                      {project.name}
                    </Text>
                    <Text size="sm" color="dimmed" lineClamp={2}>
                      {project.videoMetadata.title}
                    </Text>
                  </Stack>
                  
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <ActionIcon 
                        variant="subtle" 
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconDots size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    
                    <Menu.Dropdown>
                      <Menu.Item 
                        leftSection={<IconEdit size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/lab/${project.id}`);
                        }}
                      >
                        Open Project
                      </Menu.Item>
                      <Menu.Item 
                        leftSection={<IconShare size={14} />}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Share Project
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item 
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProject(project.id);
                        }}
                      >
                        Delete Project
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>

                {/* Project stats */}
                <Group justify="space-between" mt="md">
                  <Badge variant="light" size="sm">
                    {project.outputs.length} outputs
                  </Badge>
                  <Text size="xs" color="dimmed">
                    {new Date(project.updatedAt).toLocaleDateString()}
                  </Text>
                </Group>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      )}
    </Container>
  );
}